import React, { createContext, useContext, useCallback } from 'react';
import Api from 'lib/api';
import PropTypes from 'prop-types';
import { CANCEL_MODAL_STEPS } from '../constants/steps';
import { getNextStepForReason } from '../constants/componentMappings';
import { track } from 'lib/track';

const { ZAPIER_CANCEL_SUPPORT_WEBHOOK_URL } = process.env;
const { WLC_CANCEL_DOWNGRADE_PRICE } = process.env;
/**
 * Context for the WLC Cancel Modal component group
 * Provides shared state and actions to all components in the modal flow
 */
const CancelModalContext = createContext(null);

/**
 * Provider component that wraps the cancel modal components
 * Centralizes state management and eliminates prop drilling
 */
export const CancelModalProvider = ({
  children,
  stepManager,
  formState,
  onModalClose,
  user,
  pauseSubscription,
  downgradeSubscription,
  cancelSubscription,
  subscription,
  navigateTo
}) => {
  const subscriptionId = subscription ? subscription.id : null;

  const trackCancelStep = useCallback(
    data => {
      track('Subscription Cancel Step Completed', {
        sku: 'WLC',
        cancelRequestId: formState.cancelRequestId,
        reason: formState.selectedReason,
        subscriptionId,
        ...data
      });
    },
    [formState.selectedReason, formState.cancelRequestId, subscriptionId]
  );

  // Navigation and routing actions
  const handleProceedToCancel = useCallback(() => {
    const nextStep = getNextStepForReason(formState.selectedReason);
    trackCancelStep({ step: 'Reason Selected' });
    stepManager.goToStep(nextStep);
  }, [formState.selectedReason, stepManager]);

  const handleKeepMembership = useCallback(() => {
    trackCancelStep({ step: 'Membership Retained' });
    stepManager.goToStep(CANCEL_MODAL_STEPS.STAY_CONFIRMATION);
  }, [stepManager]);

  const handleCancelAnyway = useCallback(() => {
    trackCancelStep({ step: 'Offer Refused' });
    stepManager.goToStep(CANCEL_MODAL_STEPS.NEXT_ACTION);
  }, [stepManager]);

  const handleError = useCallback(() => {
    trackCancelStep({ step: 'Error Occurred' });
    stepManager.goToStep(CANCEL_MODAL_STEPS.ERROR_CONFIRMATION);
  }, [stepManager]);

  const handleSupportSubmitted = useCallback(() => {
    trackCancelStep({ step: 'Support Request Submitted' });
    stepManager.goToStep(CANCEL_MODAL_STEPS.SUPPORT_REQUEST_SUBMITTED);
  }, [stepManager]);

  // Billing-related actions
  const billingActions = {
    pause: useCallback(() => {
      pauseSubscription(subscriptionId, formState.selectedReason).then(resp => {
        if (resp) {
          trackCancelStep({ step: 'Subscription Paused' });
          stepManager.goToStep(CANCEL_MODAL_STEPS.PAUSE_CONFIRMATION);
        } else {
          handleError();
        }
      });
    }, [stepManager, pauseSubscription, subscriptionId, formState.selectedReason]),

    switchPlan: useCallback(() => {
      downgradeSubscription(subscriptionId, WLC_CANCEL_DOWNGRADE_PRICE).then(resp => {
        if (resp) {
          trackCancelStep({ step: 'Subscription Downgraded' });
          stepManager.goToStep(CANCEL_MODAL_STEPS.DOWNGRADE_CONFIRMATION);
        } else {
          handleError();
        }
      });
    }, [stepManager, downgradeSubscription, subscriptionId, formState.selectedReason]),

    confirmCancellation: useCallback(() => {
      cancelSubscription(subscriptionId).then(resp => {
        if (resp) {
          trackCancelStep({ step: 'Subscription Canceled' });
          stepManager.goToStep(CANCEL_MODAL_STEPS.CANCELLATION_SUCCESS);
        } else {
          handleError();
        }
      });
    }, [stepManager, cancelSubscription, subscriptionId, formState.selectedReason])
  };

  // Navigation actions (external redirects)
  const navigationActions = {
    returnToWLC: useCallback(() => {
      navigateTo('/products/wlc/current-week/');
      onModalClose();
    }, [onModalClose])
  };

  // Support actions
  const supportActions = {
    submitSupport: useCallback(
      supportMessage => {
        Api.post(
          ZAPIER_CANCEL_SUPPORT_WEBHOOK_URL,
          {
            FRNPlatformUserId: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            message: supportMessage
          },
          null,
          { removeHeaders: true, mode: 'no-cors' }
        );
        handleSupportSubmitted();
      },
      [user, onModalClose]
    )
  };
  const contextValue = {
    // Navigation state
    currentStep: stepManager.currentStep,
    isFirstStep: stepManager.isFirstStep,
    isLastStep: stepManager.isLastStep,

    // Form state
    formState,

    // Step management
    stepManager: {
      goNext: stepManager.goNext,
      goBack: stepManager.goBack,
      goToStep: stepManager.goToStep,
      reset: stepManager.reset
    },

    // Grouped actions for better organization
    actions: {
      // Core flow actions
      flow: {
        proceedToCancel: handleProceedToCancel,
        cancelAnyway: handleCancelAnyway,
        handleError: handleError,
        keepMembership: handleKeepMembership,
        goBack: stepManager.goBack
      },

      // Billing-related actions
      billing: billingActions,

      // Navigation actions (external redirects)
      navigation: navigationActions,

      // Support actions
      support: supportActions
    },
    subscription
  };

  return <CancelModalContext.Provider value={contextValue}>{children}</CancelModalContext.Provider>;
};

/**
 * Custom hook to access the cancel modal context
 * Provides type safety and better error messages
 */
export const useCancelModalContext = () => {
  const context = useContext(CancelModalContext);

  if (!context) {
    throw new Error(
      'useCancelModalContext must be used within a CancelModalProvider. ' +
        'Make sure your component is wrapped with <CancelModalProvider>.'
    );
  }

  return context;
};

/**
 * Convenience hooks for accessing specific parts of the context
 * Allows components to be more specific about what they need
 */

// Hook for accessing step navigation
export const useStepNavigation = () => {
  const { currentStep, isFirstStep, isLastStep, stepManager } = useCancelModalContext();
  return { currentStep, isFirstStep, isLastStep, ...stepManager };
};

// Hook for accessing form state
export const useFormState = () => {
  const { formState } = useCancelModalContext();
  return formState;
};

// Hook for accessing flow actions
export const useFlowActions = () => {
  const { actions } = useCancelModalContext();
  return actions.flow;
};

// Hook for accessing billing actions
export const useBillingActions = () => {
  const { actions } = useCancelModalContext();
  return actions.billing;
};

// Hook for accessing navigation actions
export const useNavigationActions = () => {
  const { actions } = useCancelModalContext();
  return actions.navigation;
};

// Hook for accessing support actions
export const useSupportActions = () => {
  const { actions } = useCancelModalContext();
  return actions.support;
};

export const useSubscription = () => {
  const { subscription } = useCancelModalContext();
  return subscription;
};

CancelModalProvider.propTypes = {
  children: PropTypes.node.isRequired,
  stepManager: PropTypes.shape({
    currentStep: PropTypes.number.isRequired,
    isFirstStep: PropTypes.bool.isRequired,
    isLastStep: PropTypes.bool.isRequired,
    goNext: PropTypes.func.isRequired,
    goBack: PropTypes.func.isRequired,
    goToStep: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired
  }).isRequired,
  formState: PropTypes.shape({
    selectedReason: PropTypes.string.isRequired,
    otherReasonText: PropTypes.string.isRequired,
    handleReasonChange: PropTypes.func.isRequired,
    handleOtherReasonChange: PropTypes.func.isRequired,
    resetForm: PropTypes.func.isRequired,
    isValid: PropTypes.bool.isRequired
  }).isRequired,
  onModalClose: PropTypes.func.isRequired
};
